import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * Login page. Minimal form, on-brand styling. Remembers the "from" path
 * passed by ProtectedRoute so the user lands back where they were trying
 * to go after a successful sign-in.
 */
export function Login() {
  const { login, authenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);

  // Default to "/" so the role-aware HomeRedirect routes each user to their
  // home: admins → /agency, EMPLOYEEs → /my-clients, clients → /home. A `from`
  // is only present on a deep-link or session-expiry bounce, where resuming the
  // original page is the desired behavior.
  const redirectTo = location.state?.from?.pathname || "/";

  // Already signed in? Bounce straight to the dashboard.
  if (authenticated) return <Navigate to={redirectTo} replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || "Sign in failed");
    }
  }

  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded bg-accent flex items-center justify-center text-white text-[14px] font-bold">
            P
          </div>
          <span className="text-[18px] font-semibold text-text-primary">
            Prism
          </span>
        </div>

        <div className="bg-surface border border-border-default rounded-card p-6">
          <div className="mb-5">
            <h1 className="text-[15px] font-semibold text-text-primary mb-1">
              Sign in
            </h1>
            <p className="text-[12px] text-text-secondary">
              Welcome back.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoFocus
              autoComplete="email"
              required
            />
            <div>
              <Field
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                required
              />
              <div className="mt-1.5 text-right">
                <Link
                  to="/forgot-password"
                  className="text-[11px] text-text-secondary hover:text-accent transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="text-[12px] text-negative rounded px-3 py-2 border"
                style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca" }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-10 rounded bg-accent text-white text-[13px] font-semibold disabled:opacity-60 hover:opacity-90 transition-opacity"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-[11px] text-text-muted text-center mt-5">
          Managed by{" "}
          <span className="font-medium text-text-secondary">
            Bayshore Marketing Group
          </span>
        </p>
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, ...rest }) {
  return (
    <div>
      <label
        className="block text-[11px] uppercase text-text-muted font-medium mb-1.5"
        style={{ letterSpacing: "0.5px" }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors"
        {...rest}
      />
    </div>
  );
}
